'use client'

import React from 'react'
import { BsInfoCircleFill } from 'react-icons/bs'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

function formatRecipeTime(minutes?: number | null) {
  const total = Number(minutes) || 0
  const hrs = Math.floor(total / 60)
  const mins = total % 60
  if (hrs > 0 && mins > 0) return `${hrs}:${String(mins).padStart(2, '0')} hr(s)`
  if (hrs > 0) return `${hrs} hr(s)`
  return `${mins} min(s)`
}

interface RecipeQuickStatsProps {
  recipe: {
    preparation_time?: number | null
    cook_time?: number | null
    number_of_servings?: number | null
    recipe_ingredients?: unknown[] | null
  }
  wakeLockEnabled: boolean
  onToggle: () => void
}

function CookModeToggleVisual({ enabled }: { enabled: boolean }) {
  return (
    <span
      className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border-2 p-0.5 transition-colors duration-200 ${
        enabled
          ? 'border-[#85C349] bg-[#85C349]'
          : 'border-[#CBD7DD] bg-[#E6E6E6]'
      }`}
    >
      <span
        className={`block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </span>
  )
}

export function RecipeQuickStats({
  recipe,
  wakeLockEnabled,
  onToggle,
}: RecipeQuickStatsProps) {
  const stats = [
    { value: formatRecipeTime(recipe?.preparation_time), label: 'Prep Time' },
    { value: formatRecipeTime(recipe?.cook_time), label: 'Cook Time' },
    { value: String(recipe?.number_of_servings || 0), label: 'Serving' },
    {
      value: String(recipe?.recipe_ingredients?.length || 0),
      label: 'Ingredients',
    },
  ]

  return (
    <div className="relative z-10 flex flex-col rounded-3xl bg-[#F3FAEB] p-4">
      <div className="grid grid-cols-2 gap-y-4 sm:flex sm:flex-row sm:items-center sm:justify-between">
        {stats.map((stat, index) => (
          <React.Fragment key={stat.label}>
            {index > 0 && (
              <div
                className="hidden h-9 w-px shrink-0 bg-[#E6E6E6] sm:block"
                aria-hidden
              />
            )}
            <div className="flex flex-col items-center gap-0.5 sm:min-w-0 sm:flex-1">
              <span className="text-base font-semibold leading-[19px] text-[#202020]">
                {stat.value}
              </span>
              <span className="text-xs font-medium leading-[15px] text-[#6B6B6B]">
                {stat.label}
              </span>
            </div>
          </React.Fragment>
        ))}

        <div
          className="hidden h-9 w-px shrink-0 bg-[#E6E6E6] sm:block"
          aria-hidden
        />

        <div className="col-span-2 flex shrink-0 flex-nowrap items-center justify-center gap-3 border-t border-[#E6E6E6] pt-3 sm:min-w-[168px] sm:flex-1 sm:border-t-0 sm:pt-0">
          <div className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
            <span className="text-[15px] font-medium text-[#202020]">
              {wakeLockEnabled ? 'Cook Mode on' : 'Cook Mode'}
            </span>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="About Cook Mode"
                  className="rounded-full p-0.5 text-[#6B6B6B] hover:text-[#202020] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#85C349] focus-visible:ring-offset-1"
                >
                  <BsInfoCircleFill size={14} />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="center"
                side="top"
                className="w-64 border-[#E6E6E6] bg-white p-3 text-sm leading-5 text-[#333333]"
              >
                Cook Mode keeps your screen awake while you follow a recipe, so it
                won&apos;t dim or lock while your hands are busy.
              </PopoverContent>
            </Popover>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={wakeLockEnabled}
            aria-label={wakeLockEnabled ? 'Turn Cook Mode off' : 'Turn Cook Mode on'}
            onClick={onToggle}
            className="cursor-pointer rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#85C349] focus-visible:ring-offset-1"
          >
            <CookModeToggleVisual enabled={wakeLockEnabled} />
          </button>
        </div>
      </div>
    </div>
  )
}
